import { supabase } from '../lib/supabase';

const APP_NAME = 'admin';
const LOG_FUNCTION = 'log-app-event';
const MAX_MESSAGE_LENGTH = 1200;

let cachedUserId = null;
let cachedUserIdAt = 0;

const safeString = (value) => {
  if (typeof value === 'string') return value;
  try {
    return JSON.stringify(value);
  } catch (err) {
    return String(value);
  }
};

const normalizeMessage = (message) => {
  const text = message ? String(message) : 'Unknown error';
  return text.length > MAX_MESSAGE_LENGTH ? `${text.slice(0, MAX_MESSAGE_LENGTH)}…` : text;
};

const resolveUserId = async () => {
  const now = Date.now();
  if (cachedUserId && now - cachedUserIdAt < 60000) {
    return cachedUserId;
  }
  try {
    const { data } = await supabase.auth.getUser();
    cachedUserId = data?.user?.id ?? null;
    cachedUserIdAt = now;
    return cachedUserId;
  } catch (err) {
    return null;
  }
};

export async function logAppEvent({
  level = 'info',
  message,
  stack,
  metadata,
  route,
  url,
  userId,
  orderId,
} = {}) {
  try {
    const resolvedUserId = userId ?? (await resolveUserId());
    await supabase.functions.invoke(LOG_FUNCTION, {
      body: {
        appName: APP_NAME,
        level,
        message: normalizeMessage(message),
        stack: stack ? String(stack) : null,
        route: route ?? window?.location?.pathname ?? null,
        url: url ?? window?.location?.href ?? null,
        userId: resolvedUserId,
        orderId: orderId ?? null,
        metadata: metadata && typeof metadata === 'object' ? metadata : { info: safeString(metadata) },
      },
    });
  } catch (err) {
    // swallow telemetry errors
  }
}

export function initAppTelemetry() {
  if (window.__APP_TELEMETRY_INIT__) return;
  window.__APP_TELEMETRY_INIT__ = true;

  window.__COMPONENT_ERROR__ = (error, errorInfo) => {
    logAppEvent({
      level: 'error',
      message: error?.message || 'Component error',
      stack: error?.stack,
      metadata: { componentStack: errorInfo?.componentStack },
    });
  };

  window.addEventListener('error', (event) => {
    logAppEvent({
      level: 'error',
      message: event?.message || 'Unhandled error',
      stack: event?.error?.stack,
      metadata: {
        filename: event?.filename,
        lineno: event?.lineno,
        colno: event?.colno,
      },
    });
  });

  window.addEventListener('unhandledrejection', (event) => {
    logAppEvent({
      level: 'error',
      message: event?.reason?.message || 'Unhandled rejection',
      stack: event?.reason?.stack,
      metadata: { reason: safeString(event?.reason) },
    });
  });
}
